import SendTrackingInfoButton from "@components/Delivery/shared/components/TrackingSection";
import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import type React from "react";
import type { PropsWithChildren } from "react";

import ClientSelector from "./FormSection/clientSelector";

type Props = {
  title?: string;
  onSubmit?: () => void;
  selectClient?: (client: any) => void;
  loading?: boolean;
  disabled?: boolean;
  modalForm?: boolean;
  showButton?: boolean;
  isValid?: boolean;
  editButton?: boolean;
  editDisabled?: boolean;
  onEditClick?: () => void;
  admin?: boolean;
  showClientSelector?: boolean;
  showTrackingButton?: boolean;
  deliveryId?: string;
};

function MainForm({
  title,
  children,
  onSubmit,
  selectClient,
  disabled = false,
  loading = false,
  showButton = true,
  isValid = true,
  editButton,
  editDisabled,
  onEditClick,
  showClientSelector = false,
  showTrackingButton = false,
  deliveryId,
}: PropsWithChildren<Props>) {
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit?.();
  };

  const handleSelect = (client: any) => {
    selectClient?.(client);
  };

  // Example inputs with gray background
  const defaultChildren = (
    <>
      <TextField label="authentication code" variant="outlined" fullWidth disabled={disabled} />
      <TextField
        label="email"
        variant="outlined"
        fullWidth
        disabled
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#e0e0e0", // Gray background
            "& fieldset": {
              borderColor: "#bdbdbd",
            },
            "&:hover fieldset": {
              borderColor: "#9e9e9e",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#9e9e9e",
            },
          },
        }}
      />
      <TextField
        label="new password"
        variant="outlined"
        fullWidth
        type="password"
        disabled={disabled}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#e0e0e0", // Gray background
            "& fieldset": {
              borderColor: "#bdbdbd",
            },
            "&:hover fieldset": {
              borderColor: "#9e9e9e",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#9e9e9e",
            },
          },
        }}
      />
      <TextField
        label="confirm password"
        variant="outlined"
        fullWidth
        type="password"
        disabled={disabled}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#e0e0e0", // Gray background
            "& fieldset": {
              borderColor: "#bdbdbd",
            },
            "&:hover fieldset": {
              borderColor: "#9e9e9e",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#9e9e9e",
            },
          },
        }}
      />
      <Typography variant="caption" sx={{ color: "#ff5c8d" }}>
        your password must contain at least: <br />8 characters <br />1 number <br />1 uppercase and
        1 lowercase <br />
        passwords match
      </Typography>
    </>
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      {title && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
          marginBottom={1}
          flexWrap="wrap"
        >
          <Typography
            variant="subtitle1"
            component="h2"
            sx={{
              flex: 1,
              textAlign: "center",
              textTransform: "capitalize",
              color: "#00244e",
              fontWeight: "bold",
            }}
          >
            {title}
          </Typography>

          {showClientSelector && (
            <Box ml={1}>
              <ClientSelector onSelect={handleSelect} />
            </Box>
          )}
          {showTrackingButton && deliveryId && (
            <Box ml={1}>
              <SendTrackingInfoButton id={deliveryId} />
            </Box>
          )}
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap={2}>
        {/* Render default children if none provided, otherwise use passed children */}
        {children || defaultChildren}
      </Box>

      {showButton && onSubmit && (
        <Stack direction="row" justifyContent="center" spacing={1.5} mt={2} mb={2}>
          {editButton && (
            <Button
              variant="contained"
              onClick={() => {
                window.scrollTo(0, 0);
                onEditClick?.();
              }}
              disabled={editDisabled || loading}
              sx={{
                bgcolor: "primary.main",
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                py: 0.5,
                fontSize: "0.875rem",
                minHeight: 36,
              }}
            >
              Modifier
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={disabled || loading || !isValid}
            sx={{
              bgcolor: "#f43f5e",
              "&:hover": {
                bgcolor: "#e11d48",
              },
              textTransform: "none",
              borderRadius: 2,
              px: 2.5,
              py: 0.5,
              fontSize: "0.875rem",
              minHeight: 36,
              color: "#fff",
              fontWeight: "bold",
            }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : "Confirmer"}
          </Button>
        </Stack>
      )}
    </form>
  );
}

export default MainForm;
